import { useCallback, useEffect, useRef, useState } from "react";
import { useSwipe } from "./useSwipe";

export const useHeroWithSlide = ({
  itemsLength,
  autoPlay,
  items,
}: {
  itemsLength: number;
  autoPlay: boolean;
  items: Array<{ type: "image" | "video" }>;
}) => {
  const slider = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const numItems = itemsLength;

  const handleNext = useCallback(() => {
    setActiveIndex((prevIndex) =>
      prevIndex === numItems - 1 ? 0 : prevIndex + 1
    );
  }, [numItems]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prevIndex) =>
      prevIndex === 0 ? numItems - 1 : prevIndex - 1
    );
  }, [numItems]);

  const setCurrentIndex = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const swipeRef = useRef<HTMLDivElement>(null);

  // Enable swipe detection
  useSwipe(swipeRef, handleNext, handlePrev);

  useEffect(() => {
    if (!autoPlay) return;

    // Capture the current value of the ref
    const currentSlider = slider.current;

    let timeoutId: NodeJS.Timeout;

    const handleAutoPlay = () => {
      const currentItem = items[activeIndex];

      if (currentItem.type === "image") {
        // For images, advance after 5 seconds
        timeoutId = setTimeout(handleNext, 5000);
      } else if (currentItem.type === "video") {
        // For videos, find the video element and listen for 'ended' event
        const videoElement = currentSlider?.querySelector(
          `#video-${activeIndex}`
        ) as HTMLVideoElement;
        if (videoElement) {
          videoElement.addEventListener("ended", handleNext);
          return () => videoElement.removeEventListener("ended", handleNext);
        }
      }
    };

    handleAutoPlay();

    return () => {
      clearTimeout(timeoutId);
      // Use the captured ref value in cleanup
      const videoElement = currentSlider?.querySelector(
        `#video-${activeIndex}`
      ) as HTMLVideoElement;
      if (videoElement) {
        videoElement.removeEventListener("ended", handleNext);
      }
    };
  }, [autoPlay, activeIndex, handleNext, items]);

  return {
    swipeRef,
    slider,
    activeIndex,
    handleNext,
    handlePrev,
    setCurrentIndex,
  };
};
